/**
 * Pure spherical-harmonic light-probe grid sampler. No three.js dependency,
 * no allocation in the hot path. Coefficients are 27 floats per probe
 * (9 SH coeffs x RGB), matching THREE.SphericalHarmonics3.coefficients order.
 *
 * Grid is one or more horizontal layers (constant Y) over the splat AABB.
 * Sampling: clamp XZ into bounds, bilinear within the layer, linear across
 * the two nearest layers (trilinear overall).
 */
export class LightProbeGrid {
  constructor(data) {
    this.bounds = data.bounds
    this.spacing = data.spacing
    this.layerYs = data.layerYs.slice().sort((a, b) => a - b)
    this.nx = data.dims.nx
    this.nz = data.dims.nz
    this.probes = data.probes
    // scratch buffers reused across calls — never reallocated per sample
    this._a = new Float32Array(27)
    this._b = new Float32Array(27)
  }

  _layerOffset(layerIndex) { return layerIndex * this.nx * this.nz }

  /** Bilinear sample within one horizontal layer into `out`. */
  _sampleLayerInto(out, layerIndex, x, z) {
    const { min } = this.bounds
    const fx = clamp((x - min[0]) / this.spacing, 0, this.nx - 1)
    const fz = clamp((z - min[2]) / this.spacing, 0, this.nz - 1)
    const ix0 = Math.floor(fx), iz0 = Math.floor(fz)
    const ix1 = Math.min(ix0 + 1, this.nx - 1)
    const iz1 = Math.min(iz0 + 1, this.nz - 1)
    const tx = fx - ix0, tz = fz - iz0
    const base = this._layerOffset(layerIndex)
    const p00 = this.probes[base + iz0 * this.nx + ix0].sh
    const p10 = this.probes[base + iz0 * this.nx + ix1].sh
    const p01 = this.probes[base + iz1 * this.nx + ix0].sh
    const p11 = this.probes[base + iz1 * this.nx + ix1].sh
    for (let i = 0; i < 27; i++) {
      const a = p00[i] + (p10[i] - p00[i]) * tx
      const b = p01[i] + (p11[i] - p01[i]) * tx
      out[i] = a + (b - a) * tz
    }
  }

  /** Trilinear sample at world (x,y,z) into the 27-float `out`. Returns `out`. */
  sampleSHInto(out, x, y, z) {
    const ys = this.layerYs
    if (ys.length === 1) {
      this._sampleLayerInto(out, 0, x, z)
      return out
    }
    const cy = clamp(y, ys[0], ys[ys.length - 1])
    let hi = 1
    while (hi < ys.length - 1 && ys[hi] < cy) hi++
    const lo = hi - 1
    const ty = (cy - ys[lo]) / (ys[hi] - ys[lo])
    this._sampleLayerInto(this._a, lo, x, z)
    this._sampleLayerInto(this._b, hi, x, z)
    for (let i = 0; i < 27; i++) out[i] = this._a[i] + (this._b[i] - this._a[i]) * ty
    return out
  }
}

function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v }
